import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { LocationOn, Public } from "@mui/icons-material";
import { gradients, boxShadows } from "../../utils/theme";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  setSelectedMall,
  selectSelectedMallId,
} from "../../store/slices/mallsSlice";
import { Mall } from "../../types/offers";

interface MallSelectorProps {
  malls: Mall[];
  onInputChange?: (value: string) => void;
  inputValue?: string;
  loading?: boolean;
  disabled?: boolean;
}

const MallSelector: React.FC<MallSelectorProps> = ({
  malls,
  onInputChange,
  inputValue: controlledInputValue,
  loading = false,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const selectedMallId = useAppSelector(selectSelectedMallId);

  const selectedMall = malls.find((mall) => mall.id === selectedMallId);

  // Use controlled value if provided, otherwise use local state
  const [localInputValue, setLocalInputValue] = useState("");
  const inputValue =
    controlledInputValue !== undefined ? controlledInputValue : localInputValue;
  const [open, setOpen] = useState(false);

  // Auto-open dropdown when results arrive after loading (but not after selection)
  useEffect(() => {
    if (!loading && malls.length > 0 && inputValue && !selectedMallId) {
      setOpen(true);
    }
  }, [loading, malls.length, inputValue, selectedMallId]);

  const handleMallChange = (event: any, value: Mall | null) => {
    if (value && value.id) {
      dispatch(setSelectedMall(value.id));
      // Clear input and close dropdown after selection to prevent API re-call
      setOpen(false);
      if (controlledInputValue === undefined) {
        setLocalInputValue("");
      }
      if (onInputChange) {
        onInputChange("");
      }
    } else {
      dispatch(setSelectedMall(null));
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    if (controlledInputValue === undefined) {
      setLocalInputValue(newInputValue);
    }
    if (onInputChange) {
      onInputChange(newInputValue);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: 600, lg: 700 },
        mx: "auto",
      }}
    >
      <Autocomplete
        value={selectedMall || null}
        onChange={handleMallChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={malls}
        loading={loading}
        disabled={disabled || loading}
        clearOnBlur={false}
        clearOnEscape={false}
        noOptionsText={
          inputValue
            ? "No malls found. Try a different search term."
            : "Start typing to search for malls..."
        }
        getOptionLabel={(option) => option.name}
        ListboxProps={{
          sx: {
            maxHeight: { xs: 250, sm: 350, md: 400 },
            bgcolor: "white",
            borderRadius: 2,
            mt: 0.5,
            boxShadow: boxShadows.deep,
            "& .MuiAutocomplete-option": {
              p: 0,
            },
          },
        }}
        sx={{
          "& .MuiAutocomplete-popupIndicator": {
            color: "primary.main",
            transition: "all 0.2s ease",
            "&:hover": {
              color: "primary.dark",
              transform: "scale(1.1)",
            },
          },
          "& .MuiAutocomplete-clearIndicator": {
            color: "error.main",
            transition: "all 0.2s ease",
            "&:hover": {
              color: "error.dark",
              transform: "rotate(90deg)",
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for a mall..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <Public
                      sx={{
                        color: "primary.main",
                        fontSize: { xs: 22, sm: 24, md: 26 },
                        transition: "all 0.2s ease",
                      }}
                    />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      sx={{ color: "primary.main" }}
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.default",
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.05rem" },
                fontWeight: 500,
                boxShadow: boxShadows.card,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                minHeight: { xs: 48, sm: 52, md: 56 },
                "& fieldset": {
                  border: "2px solid",
                  borderColor: "divider",
                },
                "&:hover": {
                  backgroundColor: "white",
                  boxShadow: boxShadows.cardHover,
                  transform: "translateY(-2px)",
                  "& fieldset": {
                    borderColor: "primary.main",
                  },
                  "& .MuiInputAdornment-root svg": {
                    transform: "scale(1.1)",
                  },
                },
                "&.Mui-focused": {
                  backgroundColor: "white",
                  boxShadow: "0 6px 30px rgba(26, 35, 126, 0.25)",
                  transform: "translateY(-2px)",
                  "& fieldset": {
                    borderColor: "primary.main",
                    borderWidth: "2px",
                  },
                  "& .MuiInputAdornment-root svg": {
                    transform: "scale(1.15)",
                  },
                },
                "& input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
                "& .MuiChip-root": {
                  maxWidth: "calc(100% - 80px)",
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderBottom: "1px solid",
              borderColor: "divider",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              overflow: "hidden",
              "&:hover": {
                background: gradients.navyLux,
                color: "white",
                "& .MuiTypography-root": {
                  color: "white !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "white !important",
                },
                "& .mall-icon-box": {
                  background: gradients.gold,
                },
              },
              "&:last-child": {
                borderBottom: "none",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                width: "100%",
              }}
            >
              <Box
                className="mall-icon-box"
                sx={{
                  width: { xs: 36, sm: 40, md: 44 },
                  height: { xs: 36, sm: 40, md: 44 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  background: gradients.navyLux,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.2s ease",
                  boxShadow: boxShadows.card,
                }}
              >
                <Public
                  sx={{
                    color: "white",
                    fontSize: { xs: 18, sm: 20, md: 22 },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    fontSize: { xs: "0.875rem", sm: "0.938rem", md: "1rem" },
                    mb: 0.25,
                  }}
                >
                  {option.name}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <LocationOn
                    sx={{
                      fontSize: { xs: 13, sm: 14, md: 15 },
                      color: "text.secondary",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", sm: "0.813rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.city}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 0.5,
                  alignItems: { xs: "flex-end", sm: "center" },
                }}
              >
                {option.activeOffers && option.activeOffers > 0 && (
                  <Chip
                    label={`${option.activeOffers} offer${
                      option.activeOffers > 1 ? "s" : ""
                    }`}
                    size="small"
                    sx={{
                      background: gradients.gold,
                      color: "white",
                      fontWeight: 700,
                      fontSize: {
                        xs: "0.625rem",
                        sm: "0.688rem",
                        md: "0.75rem",
                      },
                      height: { xs: 20, sm: 22, md: 24 },
                      boxShadow: boxShadows.card,
                      "& .MuiChip-label": {
                        px: { xs: 0.75, sm: 1 },
                      },
                    }}
                  />
                )}
                {option.activeEvents && option.activeEvents > 0 && (
                  <Chip
                    label={`${option.activeEvents} event${
                      option.activeEvents > 1 ? "s" : ""
                    }`}
                    size="small"
                    icon={<span style={{ fontSize: 12 }}>🎉</span>}
                    sx={{
                      bgcolor: "success.main",
                      color: "white",
                      fontWeight: 700,
                      fontSize: {
                        xs: "0.625rem",
                        sm: "0.688rem",
                        md: "0.75rem",
                      },
                      height: { xs: 20, sm: 22, md: 24 },
                      boxShadow: boxShadows.card,
                      "& .MuiChip-icon": {
                        color: "white",
                      },
                      "& .MuiChip-label": {
                        px: { xs: 0.75, sm: 1 },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      />
    </Box>
  );
};

export default MallSelector;
